import { useEffect, useState } from 'react';

import type { ConsentGate } from '@toothsomatics/consent-catalog';
import type { StatusCategory } from '@toothsomatics/tooth-model';

import { PageHeader, type Step as StepDefinition } from './components/PageHeader.js';
import { WHAT_THIS_IS } from './copy.js';
import { ToothChart } from './features/chart/ToothChart.js';
import { StatusPanel } from './features/chart/StatusPanel.js';
import type { ChartEntries, ImageDraft, ImageDrafts } from './features/chart/types.js';
import { hasContent } from './features/chart/types.js';
import { Questionnaire, type Answers } from './features/questionnaire/Questionnaire.js';
import { ApiError, openDraft, saveDraft, submitDraft, type DraftView } from './lib/api.js';
import { clearSession } from './lib/session.js';
import './app.css';

/** The chart the API stores, from the per-position state the screen holds. */
function toChartPayload(entries: ChartEntries) {
  return Object.entries(entries)
    .map(([number, entry]) => ({
      toothNumber: Number(number),
      selections: Object.fromEntries(
        Object.entries(entry.selections).map(([category, values]) => [category, [...(values ?? [])]]),
      ),
      note: entry.note,
    }))
    .filter(
      (entry) =>
        Object.keys(entry.selections).length > 0 || (entry.note?.trim() ?? '') !== '',
    );
}

/** The reverse: the saved draft back into the screen's state. */
function fromDraft(draft: DraftView): { entries: ChartEntries; answers: Answers } {
  const entries: Record<number, { selections: Partial<Record<StatusCategory, string[]>>; note: string }> = {};
  for (const row of draft.chart) {
    const selections = (row.selections ?? {}) as Record<string, string[]>;
    entries[row.toothNumber] = { selections, note: row.note ?? '' };
  }
  const answers: Record<string, string> = {};
  for (const row of draft.answers) answers[row.questionKey] = row.value ?? '';
  return { entries, answers };
}

/**
 * The reflective exercise: the chart, then the questionnaire.
 *
 * State is local for now. It becomes an autosaving draft against the submissions endpoints once
 * that module lands — the shapes here are already the shapes that go over the wire, so that change
 * is a transport change and not a rewrite.
 *
 * Nothing on this screen decides anything. "Save and next" moves between steps; it does not decide
 * that the chart is finished, and it never decides that the exercise may be submitted. Both are the
 * API's answers, behind its consent gates.
 */

type StepKey = 'chart' | 'questionnaire';

const STEPS: readonly StepDefinition[] = [
  { key: 'chart', label: 'Your chart' },
  { key: 'questionnaire', label: 'About you' },
];

export function App() {
  const [step, setStep] = useState<StepKey>('chart');
  const [selected, setSelected] = useState(1);
  const [entries, setEntries] = useState<ChartEntries>({});
  const [answers, setAnswers] = useState<Answers>({});
  const [images, setImages] = useState<ImageDrafts>({});
  /**
   * Every acknowledgement given so far, by gate.
   *
   * Presentation state only. The server writes its own record, against its own resolution of the
   * active document version, and refuses the operation without one — a tick here is a request to
   * record consent, never the consent itself.
   */
  const [consents, setConsents] = useState<Partial<Record<ConsentGate, boolean>>>({});
  const setConsent = (gate: ConsentGate, value: boolean) => {
    setConsents((current) => ({ ...current, [gate]: value }));
  };

  const [draft, setDraft] = useState<DraftView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<{ dueBy: string } | null>(null);

  // Open (or create) the draft on first load and fill the screen with whatever was saved before.
  useEffect(() => {
    let alive = true;
    openDraft()
      .then((view) => {
        if (!alive) return;
        setDraft(view);
        const hydrated = fromDraft(view);
        setEntries(hydrated.entries);
        setAnswers(hydrated.answers);
      })
      .catch((error: unknown) => {
        if (!alive) return;
        if (error instanceof ApiError && error.status === 401) {
          clearSession();
          window.location.replace('/login');
          return;
        }
        setLoadError(
          error instanceof ApiError && error.status === 402
            ? 'This opens once your purchase is complete.'
            : 'We could not open your reflection. Please try again in a moment.',
        );
      });
    return () => {
      alive = false;
    };
  }, []);

  const persist = async () => {
    if (!draft) return;
    await saveDraft(draft.submissionId, {
      chart: toChartPayload(entries),
      answers: Object.entries(answers).map(([questionKey, value]) => ({ questionKey, value })),
    });
  };

  const submit = async () => {
    if (!draft) {
      // Never silent: if the draft has not finished loading, say so rather than doing nothing.
      setSubmitError('Still opening your reflection — please try again in a moment.');
      return;
    }
    setBusy(true);
    setSubmitError(null);
    try {
      await persist();
      const result = await submitDraft(draft.submissionId, {
        // Echo the version and checksum the server gave us for each intake acknowledgement.
        consents: draft.consents.map((document) => ({
          gate: document.gate,
          version: document.version,
          sha256: document.sha256,
          accepted: consents[document.gate as ConsentGate] === true,
        })),
      });
      setSubmitted({ dueBy: result.dueBy });
      window.scrollTo({ top: 0, behavior: 'instant' });
    } catch (error) {
      setSubmitError(
        error instanceof ApiError
          ? error.message
          : 'We could not submit your reflection. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  const addImages = (files: readonly File[]) => {
    const drafts: ImageDraft[] = files.map((file) => ({
      id: crypto.randomUUID(),
      name: file.name,
      size: file.size,
      contentType: file.type,
      previewUrl: URL.createObjectURL(file),
      file,
    }));
    setImages((current) => ({ ...current, [selected]: [...(current[selected] ?? []), ...drafts] }));
  };

  const removeImage = (id: string) => {
    setImages((current) => {
      const forPosition = current[selected] ?? [];
      // Revoked, or the tab holds the whole file for as long as it stays open.
      const going = forPosition.find((image) => image.id === id);
      if (going) URL.revokeObjectURL(going.previewUrl);
      return { ...current, [selected]: forPosition.filter((image) => image.id !== id) };
    });
  };

  const recorded = Object.values(entries).filter(hasContent).length;

  const go = (next: StepKey) => {
    setStep(next);
    // A step change is a new screen; nobody should be left at the bottom of the previous one.
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const saveAndNext = async () => {
    setBusy(true);
    try {
      await persist();
      go('questionnaire');
    } catch {
      // A failed autosave should not trap someone on the chart; the final submit saves again.
      go('questionnaire');
    } finally {
      setBusy(false);
    }
  };

  if (loadError) {
    return (
      <>
        <PageHeader steps={STEPS} currentKey={step} />
        <main className="page">
          <div className="page__intro">
            <h1 className="page__title">Not just yet</h1>
            <p className="page__lede">{loadError}</p>
          </div>
        </main>
      </>
    );
  }

  if (submitted) {
    return (
      <>
        <PageHeader steps={STEPS} currentKey="questionnaire" />
        <main className="page">
          <div className="page__intro">
            <p className="page__eyebrow">Received</p>
            <h1 className="page__title">Your reflection is with us.</h1>
            <p className="page__lede">
              Thank you. Your chart is prepared within seven days, and we will email you the moment
              it is ready. There is nothing more you need to do.
            </p>
          </div>
        </main>
        <footer className="colophon">
          <p className="colophon__text">{WHAT_THIS_IS}</p>
        </footer>
      </>
    );
  }

  return (
    <>
      <PageHeader steps={STEPS} currentKey={step} />

      <main className="page">
        <div className="page__intro">
          <h1 className="page__title">
            {step === 'chart' ? 'Your tooth chart' : 'A few things about you'}
          </h1>
          <p className="page__lede">
            {step === 'chart'
              ? 'Take each position as it comes. Record only what you remember or have been told — nothing here needs to be exact, and “not sure” is a complete answer. You can leave and return; nothing is lost.'
              : 'The questions on the right are asked one at a time. Only your name and date of birth are needed; everything else is yours to answer or leave.'}
          </p>
        </div>

        {step === 'chart' ? (
          <>
            <div className="page__body page__body--split">
              <ToothChart entries={entries} selected={selected} onSelect={setSelected} />
              <StatusPanel
                toothNumber={selected}
                entry={entries[selected]}
                onChange={(entry) => setEntries((current) => ({ ...current, [selected]: entry }))}
                images={images[selected] ?? []}
                photoConsent={consents.photo_upload === true}
                onPhotoConsentChange={(value) => setConsent('photo_upload', value)}
                onAddImages={addImages}
                onRemoveImage={removeImage}
              />
            </div>

            <div className="actions">
              <p className="actions__status">
                {recorded === 0
                  ? 'You can move on whenever you like — the chart does not have to be complete.'
                  : `${String(recorded)} of 32 positions have something recorded.`}
              </p>
              <button
                type="button"
                className="button button--primary"
                disabled={busy}
                onClick={() => void saveAndNext()}
              >
                {busy ? 'Saving…' : 'Save and next'}
              </button>
            </div>
          </>
        ) : (
          <Questionnaire
            answers={answers}
            onChange={(key, value) => setAnswers((current) => ({ ...current, [key]: value }))}
            onBack={() => go('chart')}
            consents={consents}
            onConsentChange={setConsent}
            onSubmit={() => void submit()}
            submitting={busy}
            submitError={submitError}
          />
        )}
      </main>

      <footer className="colophon">
        <p className="colophon__text">{WHAT_THIS_IS}</p>
      </footer>
    </>
  );
}
